import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircleQuestion, Lightbulb, ClipboardList, BookOpen } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import SupportTicketForm from './SupportTicketForm'
import SuggestionForm from './SuggestionForm'
import MySubmissions from './MySubmissions'
import TrainingMaterials from './TrainingMaterials'
import supportService from '@/services/api/supportService'

// Pages where the floating button overlaps with fixed bottom bars
const HIDDEN_ROUTES = ['/role-master', '/data-quality', '/staff-order']

const SupportWidget: React.FC = () => {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('help')
  const [hasUnread, setHasUnread] = useState(false)
  const [showPulse, setShowPulse] = useState(true)

  const hideButton = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r))

  // Check for unread responses using native fetch (bypasses apiClient interceptors).
  // This prevents 401 errors from triggering the session-expiry cascade.
  const checkUnread = async () => {
    try {
      const result = await supportService.checkUnreadSilent()
      setHasUnread(result.hasUnread)
    } catch {
      // Silently fail - widget should not break the app
    }
  }

  // Stop pulse animation after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 10000)
    return () => clearTimeout(timer)
  }, [])

  const handleOpen = () => {
    setIsOpen(true)
    setShowPulse(false)
    checkUnread()
  }

  const handleSubmitSuccess = () => {
    setActiveTab('submissions')
  }

  return (
    <>
      {/* Floating Action Button — hidden on pages with fixed bottom bars */}
      <AnimatePresence>
        {!isOpen && !hideButton && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.5 }}
            onClick={handleOpen}
            className="fixed bottom-6 right-6 z-[250] flex items-center justify-center w-14 h-14 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
            aria-label="Open Support & Suggestions"
          >
            {/* Pulse ring */}
            {showPulse && (
              <span className="absolute inset-0 rounded-full animate-ping bg-green-500 opacity-30" />
            )}

            {/* Unread indicator */}
            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white" />
            )}

            <MessageCircleQuestion className="w-8 h-8 relative z-10" strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Sheet Drawer */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg p-0 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#15305D] to-[#1a4a8a] px-6 py-5 text-white flex-shrink-0">
            <SheetHeader>
              <SheetTitle className="text-white text-lg font-semibold flex items-center gap-2">
                <MessageCircleQuestion className="w-6 h-6 text-green-400" strokeWidth={2.5} />
                Support & Suggestions
              </SheetTitle>
              <SheetDescription className="text-blue-200 text-sm">
                Get help or share ideas to improve the platform
              </SheetDescription>
            </SheetHeader>
          </div>

          {/* Tabs */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 pt-3 flex-shrink-0">
                <TabsList className="w-full grid grid-cols-4 h-10">
                  <TabsTrigger value="help" className="text-xs flex items-center gap-1">
                    <MessageCircleQuestion className="w-3.5 h-3.5" />
                    Help
                  </TabsTrigger>
                  <TabsTrigger value="suggest" className="text-xs flex items-center gap-1">
                    <Lightbulb className="w-3.5 h-3.5" />
                    Suggest
                  </TabsTrigger>
                  <TabsTrigger value="learn" className="text-xs flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" />
                    Learn
                  </TabsTrigger>
                  <TabsTrigger value="submissions" className="text-xs flex items-center gap-1 relative">
                    <ClipboardList className="w-3.5 h-3.5" />
                    My Items
                    {hasUnread && (
                      <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                        !
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto">
                <TabsContent value="help" className="mt-0 h-full">
                  <SupportTicketForm onSuccess={handleSubmitSuccess} />
                </TabsContent>

                <TabsContent value="suggest" className="mt-0 h-full">
                  <SuggestionForm onSuccess={handleSubmitSuccess} />
                </TabsContent>

                <TabsContent value="learn" className="mt-0 h-full">
                  <TrainingMaterials />
                </TabsContent>

                <TabsContent value="submissions" className="mt-0 h-full">
                  <MySubmissions />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default SupportWidget
